<style scoped>
/* The panel, which sits at the page edge itself: its tabs live in its own
   header while it is open, so nothing has to stand beside it and it covers no
   more of the map than its own width. The 12px gutter is the one every floating
   surface on these pages sits in. */
.panel {
	position: absolute;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	transition: transform 0.25s ease;
	z-index: 10;
}

.panel.left {
	left: 12px;
}

.panel.left:not(.open) {
	transform: translateX(calc(-100% - 12px));
}

.panel.right {
	right: 12px;
}

.panel.right:not(.open) {
	transform: translateX(calc(100% + 12px));
}

/* Above the ledger's 20: an expanded drawer fills the page, and the Details
   pane must stay usable over it rather than become a bare header behind it. */
.panel.raised,
.rail.raised {
	z-index: 25;
}

/* The header is also the tab strip: the icons switch between the panes, the
   title names the open one, and the chevron collapses the panel back to the
   edge rail. */
.header {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 12px;
	border-bottom: 1px solid #dee2e6;
	background: #f8f9fa;
	flex-shrink: 0;
}

.header-tabs {
	display: flex;
	align-items: center;
	gap: 4px;
	flex: 0 0 auto;
}

.header-tab {
	position: relative;
	width: 30px;
	height: 30px;
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	font-size: 1rem;
	color: #495057;
	transition: background 0.15s ease;
}

.header-tab:hover {
	background: #f0f0f0;
}

.header-tab.active {
	background: #e9ecef;
	color: #212529;
}

.header-tab .badge {
	position: absolute;
	top: -4px;
	right: -4px;
	font-size: 0.65rem;
	padding: 2px 4px;
	border-radius: 10px;
}

.header-title {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-weight: 600;
	font-size: 0.95rem;
}

.header :deep(.btn) {
	padding: 2px 6px;
	line-height: 1;
}

.body {
	padding: 16px;
	overflow-y: auto;
	flex: 1;
}

/* The edge rail: the collapsed panel, and the only way back to one. It carries
   the same icons the panel's header does and stands alone only while no pane is
   open, so the edge never holds two of them. It takes the panel's own gutter,
   which is why opening a pane never moves the edge it opens from. */
.rail {
	position: absolute;
	background: #fff;
	border-radius: 6px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	padding: 6px;
	display: flex;
	flex-direction: column;
	gap: 4px;
	z-index: 11;
}

.rail.left {
	left: 12px;
}

.rail.right {
	right: 12px;
}

.rail-btn {
	position: relative;
	width: 36px;
	height: 36px;
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	font-size: 1.1rem;
	color: #495057;
	transition: background 0.15s ease;
}

.rail-btn:hover {
	background: #f0f0f0;
}

.rail-btn .badge {
	position: absolute;
	top: -6px;
	right: -6px;
	font-size: 0.65rem;
	padding: 2px 4px;
	border-radius: 10px;
}
</style>

<template>
	<!-- A closed panel is parked off the edge, so it is inert as well as
	     translated: its header carries the same tabs the rail does, and a
	     keyboard must find exactly one set of them. -->
	<div
		class="panel"
		:class="[side, { open: !!modelValue, raised }]"
		:style="bodyStyle"
		:inert="!modelValue"
	>
		<div class="header">
			<div class="header-tabs">
				<button
					v-for="tab in tabs"
					:key="tab.key"
					type="button"
					class="header-tab"
					:class="{ active: tab.key === modelValue }"
					:title="tab.title"
					:aria-pressed="tab.key === modelValue"
					@click="select(tab.key)"
				>
					<i :class="tab.icon"></i>
					<span
						v-if="tab.badgeCount !== undefined && tab.badgeCount > 0"
						class="badge rounded-pill bg-danger"
						>{{ tab.badgeCount > 99 ? "99+" : tab.badgeCount }}</span
					>
				</button>
			</div>
			<span class="header-title">{{ activeTab ? activeTab.title : "" }}</span>
			<button class="btn btn-sm btn-outline-secondary" @click="collapse">
				<i
					:class="
						side === 'left' ? 'bi bi-chevron-left' : 'bi bi-chevron-right'
					"
				></i>
			</button>
		</div>
		<div ref="bodyEl" class="body">
			<slot v-if="activeTab" :name="activeTab.key" />
		</div>
	</div>

	<div
		v-if="!modelValue"
		class="rail"
		:class="[side, { raised }]"
		:style="{ top: topOffset }"
	>
		<button
			v-for="tab in tabs"
			:key="tab.key"
			class="rail-btn"
			:title="tab.title"
			@click="select(tab.key)"
		>
			<i :class="tab.icon"></i>
			<span
				v-if="tab.badgeCount !== undefined && tab.badgeCount > 0"
				class="badge rounded-pill bg-danger"
				>{{ tab.badgeCount > 99 ? "99+" : tab.badgeCount }}</span
			>
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

export interface ToolRailTab {
	key: string;
	icon: string;
	title: string;
	badgeCount?: number;
	/** The width this tab's pane needs, where it differs from the rail's own.
	 * A list of layer rows and a column of charts want different room, and the
	 * open tab is the one that is paying for it. */
	bodyWidth?: string;
}

interface Props {
	side?: "left" | "right";
	modelValue: string | null;
	tabs: ToolRailTab[];
	topOffset?: string;
	bottomOffset?: string;
	bodyWidth?: string;
	/** Float above the ledger when it is expanded to the top. The Details pane
	 * uses this so a selection's actions stay reachable while the table takes
	 * the whole page. */
	raised?: boolean;
}

interface Emits {
	(e: "update:modelValue", value: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {
	side: "right",
	topOffset: "12px",
});

const emit = defineEmits<Emits>();

const activeTab = computed(() =>
	props.tabs.find((tab) => tab.key === props.modelValue),
);

// The panel body, the pane's one scroller, exposed so a page can reset its
// scroll when the pane's content is replaced in place (a workbench walking from
// one record to the next) rather than remounted.
const bodyEl = ref<HTMLElement | null>(null);
defineExpose({ bodyEl });

const bodyStyle = computed(() => ({
	top: props.topOffset,
	bottom: props.bottomOffset ?? (props.side === "left" ? "12px" : "320px"),
	width:
		activeTab.value?.bodyWidth ??
		props.bodyWidth ??
		(props.side === "left" ? "335px" : "320px"),
}));

function select(key: string) {
	emit("update:modelValue", key === props.modelValue ? null : key);
}

function collapse() {
	emit("update:modelValue", null);
}
</script>
